// Sprint 5 T12-v2 — store del paciente actual con cache de alergias.
// Mantiene en memoria el paciente que el auxiliar esta atendiendo en este
// momento + el set de pacientes ya consultados HOY (persistido en localStorage
// para no re-preguntar tras refresh). Las alergias se mantienen en memoria
// (se vuelven a fetch desde backend cuando se cambia de paciente).
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AlergiaResponse } from '../lib/api'

// El estado de la respuesta inicial (Δ7): el paciente puede no responder o
// declarar que desconoce sus alergias. En ambos casos NO disparamos modal otra
// vez en la misma jornada.
export type EstadoConsulta = 'CONFIRMADO_CON_ALERGIAS' | 'NIEGA' | 'NO_RESPONDE'

interface ConsultaHoy {
  pacienteId: number
  fecha: string // YYYY-MM-DD local
  estado: EstadoConsulta
}

interface PacienteActualState {
  // En memoria — no persistir.
  pacienteActualId: number | null
  alergiasPacienteActual: AlergiaResponse[]

  // Persistido — sobrevive a refresh.
  yaConsultadoHoy: ConsultaHoy[]

  // Acciones.
  setPacienteActual: (pacienteId: number | null, alergias?: AlergiaResponse[]) => void
  setAlergias: (alergias: AlergiaResponse[]) => void
  marcarConsultado: (pacienteId: number, estado: EstadoConsulta) => void
  yaFueConsultadoHoy: (pacienteId: number) => boolean
  resetConsultasViejas: () => void
  clear: () => void
}

function fechaHoyLocal(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const usePacienteActualStore = create<PacienteActualState>()(
  persist(
    (set, get) => ({
      pacienteActualId: null,
      alergiasPacienteActual: [],
      yaConsultadoHoy: [],

      setPacienteActual: (pacienteId, alergias = []) => {
        set({
          pacienteActualId: pacienteId,
          alergiasPacienteActual: alergias,
        })
      },

      setAlergias: (alergias) => {
        set({ alergiasPacienteActual: alergias })
      },

      marcarConsultado: (pacienteId, estado) => {
        const hoy = fechaHoyLocal()
        const previas = get().yaConsultadoHoy.filter(
          (c) => !(c.pacienteId === pacienteId && c.fecha === hoy),
        )
        set({
          yaConsultadoHoy: [...previas, { pacienteId, fecha: hoy, estado }],
        })
      },

      yaFueConsultadoHoy: (pacienteId) => {
        const hoy = fechaHoyLocal()
        return get().yaConsultadoHoy.some(
          (c) => c.pacienteId === pacienteId && c.fecha === hoy,
        )
      },

      resetConsultasViejas: () => {
        const hoy = fechaHoyLocal()
        set({
          yaConsultadoHoy: get().yaConsultadoHoy.filter((c) => c.fecha === hoy),
        })
      },

      clear: () => {
        set({ pacienteActualId: null, alergiasPacienteActual: [] })
      },
    }),
    {
      name: 'avante-paciente-actual',
      storage: createJSONStorage(() => localStorage),
      // Solo persistimos `yaConsultadoHoy`. El paciente activo y sus alergias
      // se vuelven a cargar al primer escaneo del dia.
      partialize: (state) => ({ yaConsultadoHoy: state.yaConsultadoHoy }),
    },
  ),
)

// Helper puro: recibe la lista de alergias y devuelve true si hay alguna
// ANAFILACTICA activa y no contradicha.
//
// Filtros aplicados:
//   - tipo === 'ALERGIA' (excluye intolerancias y preferencias)
//   - severidad === 'ANAFILACTICA'
//   - activa === true (no fue revocada)
//   - contradicha === false (medico no la descarto)
export function tieneAlergiaAnafilactica(alergias: AlergiaResponse[]): boolean {
  return alergias.some(
    (a) =>
      a.tipo === 'ALERGIA' &&
      a.severidad === 'ANAFILACTICA' &&
      a.activa === true &&
      a.contradicha === false,
  )
}

// Helper: lista de alergenos bloqueantes (severidad SEVERA o ANAFILACTICA, activos).
// Usado por OfertaMenu para tachar platos que contengan estos ingredientes.
export function alergenosBloqueantes(alergias: AlergiaResponse[]): string[] {
  return alergias
    .filter(
      (a) =>
        a.tipo === 'ALERGIA' &&
        a.activa === true &&
        a.contradicha === false &&
        (a.severidad === 'SEVERA' || a.severidad === 'ANAFILACTICA'),
    )
    .map((a) => a.alimento_nombre)
    .filter((n): n is string => Boolean(n))
}
